import { ReactNode, useEffect, useState } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { getAuthStatus, hasValidToken, shouldRequireAuth } from '@/lib/auth-utils'
import { useAuthStore } from '@/stores/auth'
import { DevLogin } from './dev-login'
import { TelegramLogin } from './telegram-login'
import { Shield } from 'lucide-react'

interface AuthGuardProps {
  children: ReactNode
}

/**
 * 全域認證守衛。
 *
 * - 啟動時一律用 checkAuth() 向後端驗證既有 token（避免過期 token 掛著完整 UI
 *   但所有請求靜默失敗）；驗證期間若 store 已是登入狀態則樂觀渲染，
 *   失敗時 checkAuth / API 層的 401 處理會重置狀態，自動退回登入畫面。
 * - 未登入時一律渲染 TelegramLogin：Mini App 內自動登入一次（主動登出除外），
 *   瀏覽器顯示 Login Widget；開發模式額外提供開發者登入。
 *   登入成功後停留在原本要去的路由。
 */
export function AuthGuard({ children }: AuthGuardProps) {
  const { isAuthenticated: storeAuthenticated, checkAuth } = useAuthStore()
  const [bootChecked, setBootChecked] = useState(() => !hasValidToken())
  const [showTelegramLogin, setShowTelegramLogin] = useState(false)

  useEffect(() => {
    let cancelled = false

    const validateOnBoot = async () => {
      if (localStorage.getItem('access_token')) {
        try {
          await checkAuth()
        } catch (error) {
          console.error('Boot auth validation failed:', error)
        }
      }
      if (!cancelled) {
        setBootChecked(true)
      }
    }

    validateOnBoot()
    return () => {
      cancelled = true
    }
  }, [checkAuth])

  // If authentication is not required, always render children
  if (!shouldRequireAuth()) {
    return <>{children}</>
  }

  // Store 已登入 → 樂觀渲染（背景驗證失敗會自動重置回登入畫面）
  if (storeAuthenticated) {
    return <>{children}</>
  }

  // 有 token 但 store 尚未確認：等待開機驗證，避免登入畫面閃現
  if (!bootChecked && hasValidToken()) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    )
  }

  // 未登入：渲染登入畫面
  const authStatus = getAuthStatus()

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="max-w-md w-full space-y-6">
        <div className="text-center">
          <Shield className="w-16 h-16 text-blue-600 mx-auto mb-4" />
          <h1 className="text-2xl font-semibold text-foreground mb-2">需要身份驗證</h1>
          <p className="text-muted-foreground">請登入以繼續使用</p>
        </div>

        {authStatus.isDev && !showTelegramLogin ? (
          <Card>
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">環境</span>
                <Badge variant="secondary">開發環境</Badge>
              </div>
              <DevLogin
                onSuccess={() => { /* store 更新後自動渲染 children */ }}
                onSwitchToTelegram={() => setShowTelegramLogin(true)}
              />
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {authStatus.isDev && (
              <div className="flex justify-end">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowTelegramLogin(false)}
                  className="text-muted-foreground"
                >
                  ← 返回開發者登入
                </Button>
              </div>
            )}
            <TelegramLogin onSuccess={() => { /* store 更新後自動渲染 children */ }} />
          </div>
        )}
      </div>
    </div>
  )
}
